import React from "react";
import { motion } from "framer-motion";
import { ArrowLeft, Calendar, Clock, Share2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import CM50Image from "../../../assets/images/CM50.jpg";

const CM50Post = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-white">
      <article className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="flex items-center justify-between mb-8">
          <motion.button
            onClick={() => navigate("/articles/blog")}
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="flex items-center text-red-600 hover:text-red-700 group"
          >
            <ArrowLeft className="w-5 h-5 mr-2 group-hover:-translate-x-1 transition-transform" />
            Back to Blog
          </motion.button>
          <motion.button
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            onClick={() => navigator.share?.({ title: document.title, url: window.location.href })}
            className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
          >
            <Share2 className="w-4 h-4" />
            Share
          </motion.button>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <div className="flex flex-wrap items-center gap-4 mb-6">
            <span className="px-4 py-1.5 bg-red-100 text-red-600 rounded-full text-sm font-medium">
              Global Leadership
            </span>
            <div className="flex items-center text-gray-500 text-sm">
              <Calendar className="w-4 h-4 mr-1.5" />
              March 2025
            </div>
            <div className="flex items-center text-gray-500 text-sm">
              <Clock className="w-4 h-4 mr-1.5" />
              6 min read
            </div>
          </div>

          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight">
            CM50: A Contract for a New Global Economy
          </h1>

          <p className="text-xl md:text-2xl text-gray-600 mb-8 leading-relaxed">
            Representing global youth at the historic CM50 conference where cooperatives were positioned as the Third Way of Economy
          </p>

          <div className="mb-12 -mx-4 sm:mx-0">
            <img
              src={CM50Image}
              alt="CM50 Conference - A Contract for a New Global Economy"
              className="w-full rounded-none sm:rounded-xl shadow-2xl"
            />
          </div>

          <div className="prose prose-lg max-w-none">
            <p className="text-gray-700 text-lg leading-relaxed mb-6">
              As a member of the International Cooperative Alliance Youth Committee and Youth Representative to the ICA Global Board, I had the privilege of participating in CM50 — a landmark gathering that brought together cooperative ministers, government representatives, global cooperative leaders, and multilateral organisations from more than 100 countries.
            </p>

            <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">What is CM50?</h2>
            <p className="text-gray-700 text-lg leading-relaxed mb-6">
              CM50 brought together cooperative ministers, government representatives, global cooperative leaders, and multilateral organisations from more than 100 countries. The platform discusses how cooperatives can contribute to economic transformation and address global challenges such as inequality, climate change, and social inclusion.
            </p>

            <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">
              What is "A Contract for a New Global Economy"?
            </h2>
            <p className="text-gray-700 text-lg leading-relaxed mb-6">
              During the conference, the global cooperative movement officially adopted <strong>A Contract for a New Global Economy</strong> — a landmark framework that proposes a new way of shaping the world economy by placing:
            </p>

            <div className="bg-gradient-to-br from-red-50 to-red-100/50 border-l-4 border-red-600 p-8 my-8 rounded-r-lg">
              <ul className="space-y-4 text-gray-800">
                <li className="flex items-start">
                  <span className="text-red-600 font-bold text-2xl mr-4 leading-none">•</span>
                  <span className="text-lg"><strong>People over profit</strong></span>
                </li>
                <li className="flex items-start">
                  <span className="text-red-600 font-bold text-2xl mr-4 leading-none">•</span>
                  <span className="text-lg"><strong>Sustainability over exploitation</strong></span>
                </li>
                <li className="flex items-start">
                  <span className="text-red-600 font-bold text-2xl mr-4 leading-none">•</span>
                  <span className="text-lg"><strong>Cooperation over competition</strong></span>
                </li>
              </ul>
            </div>

            <p className="text-gray-700 text-lg leading-relaxed mb-6">
              This contract positions cooperatives as the <strong>Third Way of Economy</strong> — beyond capitalism and socialism — where economic growth and social good coexist. It provides governments, institutions, and cooperatives a roadmap to build an economy rooted in democracy, equity, and sustainability.
            </p>

            <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">My Role & Contribution</h2>
            <p className="text-gray-700 text-lg leading-relaxed mb-6">
              Representing global youth, I was the only young leader in the room and actively contributed to conversations on how youth should be integral to the future of cooperatives and global policy-making. Under the vision and mentorship of Mukundraoji Deobhankar, I have been working towards establishing cooperatives as the third model of economy, and seeing this vision being validated and accepted at a global platform is a deeply meaningful milestone.
            </p>

            <p className="text-gray-700 text-lg leading-relaxed mb-6">
              This is not just a document — it is a commitment to building a world where development and dignity go hand in hand.
            </p>

            <div className="bg-gradient-to-br from-gray-50 to-gray-100 border-l-4 border-gray-400 rounded-r-lg p-8 my-12">
              <p className="text-gray-800 text-xl leading-relaxed italic">
                "I hope to see more youth participation in the cooperative movement, and in the next CM gatherings as well. The youth should not only inherit the future, but also help design it cooperatively."
              </p>
            </div>

            <p className="text-lg text-gray-500 mt-12 mb-8">- Harsh M. Sanghani</p>

            {/* Back to Blog CTA */}
            <div className="mt-8 pt-8 border-t border-gray-200">
              <button
                onClick={() => navigate("/articles/blog")}
                className="inline-flex items-center gap-2 px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium"
              >
                <ArrowLeft className="w-5 h-5" />
                Read More Articles
              </button>
            </div>
          </div>
        </motion.div>
      </article>
    </div>
  );
};

export default CM50Post;
